import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import {
  AlertTriangle,
  Package,
  ChevronLeft,
  ChevronRight,
  UtensilsCrossed,
  Box,
  Info,
  Calculator,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { STANDARD_UNIT_OPTIONS } from '@/lib/unitConversions';
import { convertUnits } from '@/lib/unitConversions';

interface COGSVendorTrainingStep5ManualCostsProps {
  onNext: () => void;
  onPrevious: () => void;
  selectedVendors: string[];
}

interface UnmappedIngredient {
  id: string;
  name: string;
  category: string;
  standard_unit: string;
  /** When present, this item was already saved (e.g. on revisit); show in table and treat as saved */
  cost_per_standard_unit?: number;
}

const UNIT_OPTIONS = STANDARD_UNIT_OPTIONS;

function normalizeCategory(cat: string): string {
  const lower = (cat || '').toLowerCase().trim();
  if (lower === 'food cost' || lower === 'food' || lower === 'beverage cost' || lower === 'beverage' || lower === 'drinks') return 'Food Cost';
  if (lower === 'packaging') return 'Packaging';
  if (lower === 'supplies') return 'Supplies';
  if (lower === 'non-business' || lower === 'nonbusiness') return 'Non-Business';
  return cat || 'Food Cost';
}

function isFoodCostOrPackaging(category: string): boolean {
  const n = normalizeCategory(category);
  return n === 'Food Cost' || n === 'Packaging';
}

function isSuppliesOrNonBusiness(category: string): boolean {
  const n = normalizeCategory(category);
  return n === 'Supplies' || n === 'Non-Business';
}

export const COGSVendorTrainingStep5ManualCosts = ({
  onNext,
  onPrevious,
  selectedVendors,
}: COGSVendorTrainingStep5ManualCostsProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [unmappedIngredients, setUnmappedIngredients] = useState<UnmappedIngredient[]>([]);
  const [costingCurrentPage, setCostingCurrentPage] = useState(1);
  const [manualCosts, setManualCosts] = useState<Record<string, string>>({});
  const [manualPurchaseQty, setManualPurchaseQty] = useState<Record<string, string>>({});
  const [manualPurchaseUnit, setManualPurchaseUnit] = useState<Record<string, string>>({});
  const [manualRecipeUnit, setManualRecipeUnit] = useState<Record<string, string>>({});
  const [savedCosts, setSavedCosts] = useState<Record<string, number>>({});
  const [savedPurchaseQty, setSavedPurchaseQty] = useState<Record<string, string>>({});
  const [savedPurchaseUnit, setSavedPurchaseUnit] = useState<Record<string, string>>({});
  const [savedRecipeUnit, setSavedRecipeUnit] = useState<Record<string, string>>({});
  const [savedCostPerRecipeUnit, setSavedCostPerRecipeUnit] = useState<Record<string, number>>({});
  const [savingIngredientId, setSavingIngredientId] = useState<string | null>(null);
  const costingItemsPerPage = 10;

  const foodCostAndPackaging = useMemo(
    () => unmappedIngredients.filter((i) => isFoodCostOrPackaging(i.category)),
    [unmappedIngredients],
  );
  const suppliesAndNonBusiness = useMemo(
    () => unmappedIngredients.filter((i) => isSuppliesOrNonBusiness(i.category)),
    [unmappedIngredients],
  );

  useEffect(() => {
    if (selectedVendors && selectedVendors.length > 0) {
      loadUnmappedIngredients();
    } else {
      setLoading(false);
      setUnmappedIngredients([]);
    }
  }, [selectedVendors]);

  const loadUnmappedIngredients = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const [{ data: mappingsData }, { data: allInventoryItems }] = await Promise.all([
        supabase
          .from('vendor_ingredient_mappings')
          .select(
            'ingredient_id, vendor_id, vendor_product_description, recipe_unit, cost_per_recipe_unit, is_coupon, cluster_id',
          )
          .eq('company_id', companyId)
          .in('vendor_id', selectedVendors),
        supabase
          .from('ingredients')
          .select('id, name, category, standard_unit, cost_per_standard_unit')
          .eq('company_id', companyId),
      ]);

      const allMappings = mappingsData || [];
      const mappedIngredientIds = new Set(
        (allMappings as any[]).map((m) => m.ingredient_id).filter(Boolean),
      );
      const mappedIngredientNames = new Set(
        (allInventoryItems || [])
          .filter((item: any) => mappedIngredientIds.has(item.id))
          .map((item: any) => (item.name || '').toLowerCase().trim())
          .filter(Boolean),
      );
      const mappedDescriptions = new Set(
        (allMappings as any[])
          .map((m) => (m.vendor_product_description || '').toLowerCase().trim())
          .filter(Boolean),
      );

      const unmapped: UnmappedIngredient[] = (allInventoryItems || [])
        .filter((item: any) => {
          if (mappedIngredientIds.has(item.id)) return false;
          const normalizedName = (item.name || '').toLowerCase().trim();
          if (!normalizedName) return true;
          if (mappedIngredientNames.has(normalizedName)) return false;
          if (mappedDescriptions.has(normalizedName)) return false;
          for (const desc of mappedDescriptions) {
            if (desc.includes(normalizedName) || normalizedName.includes(desc)) return false;
          }
          return true;
        })
        .map((item: any) => ({
          id: item.id as string,
          name: item.name as string,
          category: normalizeCategory((item.category as string) || 'food cost'),
          standard_unit: (item.standard_unit as string) || 'each',
          cost_per_standard_unit:
            item.cost_per_standard_unit != null && item.cost_per_standard_unit > 0
              ? Number(item.cost_per_standard_unit)
              : undefined,
        }));

      setUnmappedIngredients(unmapped);
      if (!silent) {
        const initialSavedCosts: Record<string, number> = {};
        const initialSavedPurchaseQty: Record<string, string> = {};
        const initialSavedPurchaseUnit: Record<string, string> = {};
        const initialSavedRecipeUnit: Record<string, string> = {};
        const initialSavedCostPerRecipeUnit: Record<string, number> = {};
        unmapped.forEach((item) => {
          if (item.cost_per_standard_unit != null && item.cost_per_standard_unit > 0) {
            initialSavedCosts[item.id] = item.cost_per_standard_unit;
            initialSavedPurchaseQty[item.id] = '1';
            initialSavedPurchaseUnit[item.id] = item.standard_unit;
            initialSavedRecipeUnit[item.id] = item.standard_unit;
            initialSavedCostPerRecipeUnit[item.id] = item.cost_per_standard_unit;
          }
        });
        setSavedCosts(initialSavedCosts);
        setSavedPurchaseQty(initialSavedPurchaseQty);
        setSavedPurchaseUnit(initialSavedPurchaseUnit);
        setSavedRecipeUnit(initialSavedRecipeUnit);
        setSavedCostPerRecipeUnit(initialSavedCostPerRecipeUnit);
        setManualCosts((prev) => {
          const next = { ...prev };
          unmapped.forEach((item) => {
            if (item.cost_per_standard_unit != null && item.cost_per_standard_unit > 0) {
              next[item.id] = item.cost_per_standard_unit.toString();
            }
          });
          return next;
        });
        setManualPurchaseQty((prev) => {
          const next = { ...prev };
          unmapped.forEach((item) => {
            if (item.cost_per_standard_unit != null && item.cost_per_standard_unit > 0) {
              next[item.id] = '1';
            }
          });
          return next;
        });
        setManualPurchaseUnit((prev) => {
          const next = { ...prev };
          unmapped.forEach((item) => {
            if (item.cost_per_standard_unit != null && item.cost_per_standard_unit > 0) {
              next[item.id] = item.standard_unit;
            }
          });
          return next;
        });
        setManualRecipeUnit((prev) => {
          const next = { ...prev };
          unmapped.forEach((item) => {
            if (item.cost_per_standard_unit != null && item.cost_per_standard_unit > 0) {
              next[item.id] = item.standard_unit;
            }
          });
          return next;
        });
      }
      if (silent) {
        const costingList = unmapped.filter((i) => isFoodCostOrPackaging(i.category));
        setCostingCurrentPage((p) =>
          Math.min(p, Math.max(1, Math.ceil(costingList.length / costingItemsPerPage))),
        );
      }
    } catch (error: any) {
      console.error('Error loading unmapped ingredients:', error);
      if (!silent) {
        toast({
          title: 'Error',
          description: 'Failed to load inventory items that need manual costs',
          variant: 'destructive',
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getPurchaseQty = (item: UnmappedIngredient) =>
    manualPurchaseQty[item.id] ?? savedPurchaseQty[item.id] ?? '1';
  const getPurchaseUnit = (item: UnmappedIngredient) =>
    manualPurchaseUnit[item.id] ?? savedPurchaseUnit[item.id] ?? item.standard_unit;
  const getRecipeUnit = (item: UnmappedIngredient) =>
    manualRecipeUnit[item.id] ?? savedRecipeUnit[item.id] ?? item.standard_unit;
  const getCost = (item: UnmappedIngredient) =>
    manualCosts[item.id] ??
    ((savedCosts[item.id] != null ? savedCosts[item.id].toString() : '') ||
      (item.cost_per_standard_unit != null ? item.cost_per_standard_unit.toString() : ''));
  /**
   * Same formula as COGSVendorTrainingStep5 (normal flow):
   *   totalCost (purchase price) / convertUnits(purchaseQty, purchaseUnit, recipeUnit)
   * "Cost ($)" is the total purchase price; purchaseQty + purchaseUnit describe the quantity
   * bought for that price, used only for unit conversion.
   */
  const computeCostPerRecipeUnit = (item: UnmappedIngredient): number | null => {
    const costStr = getCost(item);
    const totalCost = costStr ? parseFloat(costStr) : NaN;
    if (Number.isNaN(totalCost) || totalCost <= 0) return null;
    const purchaseQtyStr = getPurchaseQty(item);
    const purchaseQty = purchaseQtyStr ? parseFloat(purchaseQtyStr) : 1;
    if (Number.isNaN(purchaseQty) || purchaseQty <= 0) return null;
    const purchaseUnit = getPurchaseUnit(item);
    const recipeUnit = getRecipeUnit(item);
    const recipeUnitsFromPurchase = convertUnits(purchaseQty, purchaseUnit, recipeUnit);
    if (recipeUnitsFromPurchase === 0) return null;
    return totalCost / recipeUnitsFromPurchase;
  };

  const getRecipeCalcBreakdown = (item: UnmappedIngredient) => {
    const costStr = getCost(item);
    const totalCost = costStr ? parseFloat(costStr) : NaN;
    const purchaseQtyStr = getPurchaseQty(item);
    const qty = purchaseQtyStr ? parseFloat(purchaseQtyStr) : NaN;
    const pUnit = getPurchaseUnit(item);
    const rUnit = getRecipeUnit(item);
    if (Number.isNaN(totalCost) || totalCost <= 0 || Number.isNaN(qty) || qty <= 0 || !pUnit || !rUnit)
      return null;
    const recipeUnits = convertUnits(qty, pUnit, rUnit);
    if (recipeUnits === 0) return null;
    const result = totalCost / recipeUnits;
    return { totalCost, qty, pUnit, rUnit, recipeUnits, result };
  };

  const handleSaveManualCost = async (ingredientId: string) => {
    const item = foodCostAndPackaging.find((i) => i.id === ingredientId);
    if (!item) return;

    const costStr = getCost(item);
    const totalCostParsed = costStr ? parseFloat(costStr) : NaN;
    if (!costStr || Number.isNaN(totalCostParsed) || totalCostParsed <= 0) {
      toast({
        title: 'Enter a valid cost',
        description: 'Please enter a positive total purchase cost for this item.',
        variant: 'destructive',
      });
      return;
    }

    const purchaseQtyStr = getPurchaseQty(item);
    const purchaseQtyParsed = purchaseQtyStr ? parseFloat(purchaseQtyStr) : NaN;
    if (Number.isNaN(purchaseQtyParsed) || purchaseQtyParsed <= 0) {
      toast({
        title: 'Enter a valid purchase qty',
        description: 'Please enter a positive purchase quantity for unit conversion.',
        variant: 'destructive',
      });
      return;
    }

    const purchaseQty = purchaseQtyStr.trim() || '1';
    const purchaseUnit = getPurchaseUnit(item);
    const recipeUnit = getRecipeUnit(item);
    const computed = computeCostPerRecipeUnit(item);
    const costPerRecipe = computed ?? totalCostParsed;

    try {
      setSavingIngredientId(ingredientId);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const { error } = await supabase
        .from('ingredients')
        .update({
          standard_unit: recipeUnit,
          cost_per_standard_unit: costPerRecipe,
        })
        .eq('company_id', companyId)
        .eq('id', ingredientId);

      if (error) throw error;

      const itemName = item.name;

      setSavedCosts((prev) => ({ ...prev, [ingredientId]: totalCostParsed }));
      setSavedPurchaseQty((prev) => ({ ...prev, [ingredientId]: purchaseQty }));
      setSavedPurchaseUnit((prev) => ({ ...prev, [ingredientId]: purchaseUnit }));
      setSavedRecipeUnit((prev) => ({ ...prev, [ingredientId]: recipeUnit }));
      setSavedCostPerRecipeUnit((prev) => ({ ...prev, [ingredientId]: costPerRecipe }));
      setManualCosts((prev) => ({ ...prev, [ingredientId]: totalCostParsed.toString() }));
      setManualPurchaseQty((prev) => ({ ...prev, [ingredientId]: purchaseQty }));
      setManualPurchaseUnit((prev) => ({ ...prev, [ingredientId]: purchaseUnit }));
      setManualRecipeUnit((prev) => ({ ...prev, [ingredientId]: recipeUnit }));

      toast({
        title: 'Cost saved',
        description: `Saved $${costPerRecipe.toFixed(2)}/${recipeUnit} for ${itemName}. You can revise anytime by editing and saving again.`,
      });
    } catch (error: any) {
      console.error('Error saving manual cost:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save manual cost',
        variant: 'destructive',
      });
    } finally {
      setSavingIngredientId(null);
    }
  };

  const hasCostingRowChanged = (item: UnmappedIngredient): boolean => {
    const costStr = getCost(item);
    const cost = costStr ? parseFloat(costStr) : NaN;
    const savedCost = savedCosts[item.id];
    if (savedCost == null && !(item.cost_per_standard_unit != null && item.cost_per_standard_unit > 0))
      return !Number.isNaN(cost) && cost > 0;
    if (Number.isNaN(cost) || cost <= 0) return false;
    if (savedCost != null && cost !== savedCost) return true;
    const purchaseQty = getPurchaseQty(item);
    if (purchaseQty !== (savedPurchaseQty[item.id] ?? '1')) return true;
    const purchaseUnit = getPurchaseUnit(item);
    if (purchaseUnit !== (savedPurchaseUnit[item.id] ?? item.standard_unit)) return true;
    const recipeUnit = getRecipeUnit(item);
    if (recipeUnit !== (savedRecipeUnit[item.id] ?? item.standard_unit)) return true;
    return false;
  };

  const canSaveCostingRow = (item: UnmappedIngredient): boolean => {
    const costStr = getCost(item);
    const cost = costStr ? parseFloat(costStr) : NaN;
    if (Number.isNaN(cost) || cost <= 0) return false;
    return hasCostingRowChanged(item);
  };

  const allCostingItemsSaved =
    foodCostAndPackaging.length === 0 ||
    foodCostAndPackaging.every(
      (item) =>
        savedCosts[item.id] != null ||
        (item.cost_per_standard_unit != null && item.cost_per_standard_unit > 0),
    );

  const handleContinue = () => {
    if (!allCostingItemsSaved) {
      toast({
        title: 'Manual costs required',
        description:
          'Please add and save costing for each Food Cost and Packaging item in the first table. Supplies and Non-Business items do not require cost data.',
        variant: 'destructive',
      });
      return;
    }
    onNext();
  };

  if (loading) {
    return (
      <div className="space-y-6 py-8">
        <div className="space-y-2">
          <Skeleton className="h-6 w-96" />
          <Skeleton className="h-4 w-full max-w-2xl" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-40" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Fill Missing Inventory Costs</h3>
        <p className="text-sm text-muted-foreground">
          Some inventory items from your Module 3 inventory were not found in any vendor receipts.
          Items are split below: <strong>Food Cost &amp; Packaging</strong> need full costing input for COGS and recipe costing;
          <strong> Supplies &amp; Non-Business</strong> are listed for your awareness only and do not require cost data.
          If any of these items appear on future uploaded receipts, the system will flag them for you.
        </p>
      </div>

      {unmappedIngredients.length > 0 ? (
        <>
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>{unmappedIngredients.length} inventory item{unmappedIngredients.length !== 1 ? 's' : ''}</strong> from Module 3
              were not found in any receipt. {foodCostAndPackaging.length > 0 && (
                <>Enter purchase unit, cost, recipe unit, and cost per recipe unit for <strong>Food Cost &amp; Packaging</strong> items below.</>
              )}
              {suppliesAndNonBusiness.length > 0 && (
                <> Supplies and Non-Business items are listed in a separate table for reference only.</>
              )}
            </AlertDescription>
          </Alert>

          {/* Section 1: Food Cost & Packaging – full costing */}
          {foodCostAndPackaging.length > 0 && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <UtensilsCrossed className="h-5 w-5 text-blue-600" />
                  Food Cost &amp; Packaging – Manual Costing ({foodCostAndPackaging.length})
                </CardTitle>
                <CardDescription>
                  These items affect COGS and recipe costing. Enter purchase qty, purchase unit, total cost ($), and recipe unit.
                  Cost per Recipe Unit is auto-calculated from unit conversion — same formula as in Step 4.
                  Hover the <Calculator className="inline h-3 w-3 text-blue-500" /> icon to see the full breakdown. You can revise any saved row by editing and saving again.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCostingCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={costingCurrentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    Page {costingCurrentPage} of{' '}
                    {Math.max(1, Math.ceil(foodCostAndPackaging.length / costingItemsPerPage))}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCostingCurrentPage((p) =>
                        Math.min(
                          Math.max(1, Math.ceil(foodCostAndPackaging.length / costingItemsPerPage)),
                          p + 1,
                        ),
                      )
                    }
                    disabled={
                      costingCurrentPage >=
                      Math.max(1, Math.ceil(foodCostAndPackaging.length / costingItemsPerPage))
                    }
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="py-2">Inventory Item</TableHead>
                      <TableHead className="py-2">Category</TableHead>
                      <TableHead className="py-2 w-[90px]">Purchase Qty</TableHead>
                      <TableHead className="py-2 w-[100px]">Purchase Unit</TableHead>
                      <TableHead className="py-2 w-[120px]">Cost ($)</TableHead>
                      <TableHead className="py-2 w-[100px]">Recipe Unit</TableHead>
                      <TableHead className="py-2 w-[120px]">Cost per Recipe Unit ($)</TableHead>
                      <TableHead className="w-[80px] py-2" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {foodCostAndPackaging
                      .slice(
                        (costingCurrentPage - 1) * costingItemsPerPage,
                        costingCurrentPage * costingItemsPerPage,
                      )
                      .map((item) => {
                        const costPerRecipeComputed = computeCostPerRecipeUnit(item);
                        const breakdown = getRecipeCalcBreakdown(item);
                        return (
                          <TableRow key={item.id} className="hover:bg-muted/50">
                            <TableCell className="py-1.5 font-medium">{item.name}</TableCell>
                            <TableCell className="py-1.5">{item.category}</TableCell>
                            <TableCell className="py-1.5">
                              <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                placeholder="1"
                                className="h-8 w-full"
                                value={getPurchaseQty(item)}
                                onChange={(e) =>
                                  setManualPurchaseQty((prev) => ({ ...prev, [item.id]: e.target.value }))
                                }
                              />
                            </TableCell>
                            <TableCell className="py-1.5">
                              <Select
                                value={getPurchaseUnit(item)}
                                onValueChange={(v) =>
                                  setManualPurchaseUnit((prev) => ({ ...prev, [item.id]: v }))
                                }
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {UNIT_OPTIONS.map((u) => (
                                    <SelectItem key={u} value={u}>
                                      {u}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="py-1.5">
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground font-medium">$</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="e.g. 2"
                                  className="h-8 flex-1"
                                  value={getCost(item)}
                                  onChange={(e) =>
                                    setManualCosts((prev) => ({ ...prev, [item.id]: e.target.value }))
                                  }
                                />
                              </div>
                            </TableCell>
                            <TableCell className="py-1.5">
                              <Select
                                value={getRecipeUnit(item)}
                                onValueChange={(v) =>
                                  setManualRecipeUnit((prev) => ({ ...prev, [item.id]: v }))
                                }
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {UNIT_OPTIONS.map((u) => (
                                    <SelectItem key={u} value={u}>
                                      {u}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="py-1.5">
                              {costPerRecipeComputed != null ? (
                                <TooltipProvider delayDuration={200}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="bg-primary/5 border border-primary/20 rounded-md h-8 px-2 flex items-center gap-1 cursor-help">
                                        <span className="font-mono font-semibold text-sm">
                                          ${costPerRecipeComputed.toFixed(2)}
                                        </span>
                                        <Calculator className="h-3 w-3 text-blue-500 shrink-0 ml-auto" />
                                      </div>
                                    </TooltipTrigger>
                                    {breakdown && (
                                      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed p-3">
                                        <p className="font-semibold mb-1">Recipe Cost Calculation</p>
                                        <p>
                                          Purchase: {breakdown.qty} {breakdown.pUnit} → {breakdown.recipeUnits.toFixed(2)} {breakdown.rUnit}
                                        </p>
                                        <p className="font-semibold mt-1">
                                          ${breakdown.totalCost.toFixed(2)} ÷ {breakdown.recipeUnits.toFixed(2)} {breakdown.rUnit}
                                          {' '}= ${breakdown.result.toFixed(2)}/{breakdown.rUnit}
                                        </p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <div className="bg-muted/50 rounded-md h-8 px-2 flex items-center">
                                  <span className="text-xs text-muted-foreground">Auto</span>
                                </div>
                              )}
                              {breakdown && (
                                <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                                  ${breakdown.totalCost.toFixed(2)} ÷ {breakdown.recipeUnits.toFixed(2)} {breakdown.rUnit}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="py-1.5 text-right">
                              <Button
                                size="sm"
                                variant="secondary"
                                className="h-8"
                                onClick={() => handleSaveManualCost(item.id)}
                                disabled={savingIngredientId === item.id || !canSaveCostingRow(item)}
                              >
                                {savingIngredientId === item.id ? 'Saving...' : 'Save'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Section 2: Supplies & Non-Business – read-only */}
          {suppliesAndNonBusiness.length > 0 && (
            <Card className="border-slate-200 bg-slate-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Box className="h-5 w-5 text-slate-600" />
                  Supplies &amp; Non-Business – For Your Reference ({suppliesAndNonBusiness.length})
                </CardTitle>
                <CardDescription>
                  These items do not require cost input for this module. They are listed so you know they were not found in any receipt.
                  If they appear on future uploaded receipts, the system will flag them for you to categorize.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="py-2">Inventory Item</TableHead>
                      <TableHead className="py-2">Category</TableHead>
                      <TableHead className="py-2">Standard Unit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliesAndNonBusiness.map((item) => (
                      <TableRow key={item.id} className="hover:bg-muted/30">
                        <TableCell className="py-1.5 font-medium">{item.name}</TableCell>
                        <TableCell className="py-1.5">{item.category}</TableCell>
                        <TableCell className="py-1.5">{item.standard_unit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              All items listed here may appear on future uploaded receipts. When they do, the system will flag them so you can map or update costing as needed.
            </AlertDescription>
          </Alert>
        </>
      ) : (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="text-center py-6 space-y-2">
              <CardTitle className="text-base">All inventory items are costed</CardTitle>
              <p className="text-sm text-muted-foreground">
                Every inventory item from Module 3 either appears in receipts or already has manual cost data.
                You can continue to review your trained mappings.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          Previous Step
        </Button>
        <Button
          onClick={handleContinue}
          disabled={
            savingIngredientId !== null ||
            (foodCostAndPackaging.length > 0 && !allCostingItemsSaved)
          }
        >
          Continue to Review Mappings
        </Button>
      </div>
    </div>
  );
};
